import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";

/** Import-only synthetic chip; stripped before `PopupImportGroupInput` (not a GraphQL enum). */
export const POPUP_IMPORT_SEGMENT_ALL_VALUE =
  "__POPUP_IMPORT_SEGMENT_ALL__" as const;

const trimCell = (v: string): string => v.trim();

/**
 * Fallback map for raw DB IDs stored in segment columns.
 * Each key is a raw DB value; the array lists all GraphQL enum names it can map to
 * (resolving ambiguity via the `allowed` set at call time).
 */
const SEGMENT_DB_ID_CANDIDATES: Record<string, string[]> = {
  // Device (1000=MOBILE, 2000=TABLET, 3000=PC)
  // Platform (1000=IOS, 2000=WINDOWS, 3000=ANDROID, 4000=MAC, 5000=LINUX, 9999=OTHER)
  // Browser (1000=IE, 2000=CHROME, 3000=SAFARI, 4000=FIREFOX, 5000=EDGE, 9999=OTHER)
  "1000": ["MOBILE", "IOS", "IE"],
  "2000": ["TABLET", "WINDOWS", "CHROME"],
  "3000": ["PC", "ANDROID", "SAFARI"],
  "4000": ["MAC", "FIREFOX"],
  "5000": ["LINUX", "EDGE"],
  "9999": ["OTHER"],
  // Gender
  YuOvZzOtrag: ["MALE"],
  MSjKrRC4FTY: ["FEMALE"],
  // AgeRange
  "8B6JsG6QWYM": ["LT12"],
  "hglDj2+aeQE": ["GT12LT19"],
  cshmz6PJfOA: ["GT20LT24"],
  "Ur6+AwKUVFU": ["GT25LT29"],
  "1oJV1nXVQnI": ["GT30LT34"],
  BMsHL4txy0g: ["GT35LT39"],
  Co7ZoKZu6to: ["GT40LT44"],
  LpJTCtHESl8: ["GT45LT49"],
  pXzxOaN3sAI: ["GT50LT54"],
  alxhQBq8HxA: ["GT55LT59"],
  // AnnualIncome
  Wtwgat4wtAA: ["LT200"],
  HgaDyzEMA2c: ["GT200LT400"],
  "OjG4ET+R8fE": ["GT400LT600"],
  "69hTtVf/Mfw": ["GT600LT800"],
  kzGtJpkqsBw: ["GT800LT1000"],
  "0GXHm9n+FQo": ["GT1000LT1200"],
  PYlOhF49mDY: ["GT1200LT1500"],
  OPT47xGABSk: ["GT1500LT2000"],
  "/hmabYsVAYE": ["GT2000"],
};

/** Split CSV cell like `splitEnums` in convertFunctions; keep only GraphQL enum values.
 *  Falls back to `SEGMENT_DB_ID_CANDIDATES` for legacy exports with raw DB IDs. */
export function parseGraphqlEnumListCell(
  raw: string,
  allowed: ReadonlySet<string>
): string[] {
  const parts = raw
    .split(/[;,|]/)
    .map((s) => trimCell(s))
    .filter(Boolean);
  return parts
    .map((p) => {
      if (allowed.has(p)) return p;
      const candidates = SEGMENT_DB_ID_CANDIDATES[p];
      if (candidates) {
        const match = candidates.find((c) => allowed.has(c));
        if (match) return match;
      }
      return null;
    })
    .filter((p): p is string => p !== null);
}

export function filterGroupEnumValues(
  values: string[] | undefined,
  allowed: ReadonlySet<string>
): string[] {
  return (values ?? []).filter((x) => allowed.has(x));
}

/** True when the selection includes every allowed enum (unrestricted segment — omit on import API). */
export function isCompleteEnumSelection(
  values: string[] | undefined,
  allowed: ReadonlySet<string>
): boolean {
  const filtered = filterGroupEnumValues(values, allowed);
  if (filtered.length !== allowed.size) return false;
  const uniq = new Set(filtered);
  if (uniq.size !== allowed.size) return false;
  return Array.from(allowed).every((a) => uniq.has(a));
}

/** Empty CSV cell, or a cell that lists every allowed value → one 「全て」 chip in import UI. */
export function normalizeSegmentEnumImportCell(
  raw: string,
  allowed: ReadonlySet<string>
): string[] {
  const parsed = parseGraphqlEnumListCell(raw, allowed);
  if (parsed.length === 0) return [POPUP_IMPORT_SEGMENT_ALL_VALUE];
  if (isCompleteEnumSelection(parsed, allowed))
    return [POPUP_IMPORT_SEGMENT_ALL_VALUE];
  return parsed;
}

export const POPUP_IMPORT_ALLOWED_DEVICE = new Set(
  GraphQLEnums.Device.map((o) => o.value)
);
export const POPUP_IMPORT_ALLOWED_PLATFORM = new Set(
  GraphQLEnums.Platform.map((o) => o.value)
);
export const POPUP_IMPORT_ALLOWED_BROWSER = new Set(
  GraphQLEnums.Browser.map((o) => o.value)
);
export const POPUP_IMPORT_ALLOWED_GENDER = new Set(
  GraphQLEnums.Gender.map((o) => o.value)
);
export const POPUP_IMPORT_ALLOWED_AGE_RANGE = new Set(
  GraphQLEnums.AgeRange.map((o) => o.value)
);
export const POPUP_IMPORT_ALLOWED_ANNUAL_INCOME = new Set(
  GraphQLEnums.AnnualIncome.map((o) => o.value)
);
